import React from "react";

import { NoteSection, sectionConfig } from "./DSNoteInput";
import { DSDiffActions } from "./DSNoteInputDiffActions";
import { MarkdownContent } from "../atoms/MarkdownContent";
import { DSRichTextToolbar } from "../molecules/DSRichTextToolbar";

export interface DSDiffViewProps {
  /** The AI-generated "after" content */
  afterContent: string | null;
  /** The original "before" content */
  beforeContent: string;
  /** The section type - used to look up styling automatically */
  section: NoteSection;
  /** Optional label override (defaults to section's label) */
  label?: string;
  /** Whether to use compact layout (stacked instead of side-by-side) */
  isCompact?: boolean;
  /** Whether content is still loading/streaming */
  isLoading?: boolean;
  /** Callback when Accept button is clicked */
  onAccept: () => void;
  /** Callback when Reject button is clicked */
  onReject: () => void;
  /** Test ID for automated testing */
  dataTestId: string;
}

/**
 * Side-by-side diff view for comparing AI-generated content with original content.
 * Shows "After" content on the left and "Before" content on the right in default mode.
 * In compact mode, shows After on top and Before on bottom.
 */
export const DSDiffView = ({
  afterContent,
  beforeContent,
  section,
  label,
  isCompact = false,
  isLoading = false,
  onAccept,
  onReject,
  dataTestId,
}: DSDiffViewProps) => {
  const config = sectionConfig[section];
  const displayLabel = label || config.label;
  const layoutClass = isCompact
    ? "flex flex-col gap-sm"
    : "flex flex-row gap-md h-full";

  return (
    <div
      className="flex flex-row items-start gap-sm"
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
    >
      {/* Letter badge for compact mode */}
      {isCompact && config.letter && (
        <span
          className={`text-xl font-bold shrink-0 ${config.textClass}`}
          aria-label={displayLabel}
        >
          {config.letter}
        </span>
      )}

      <div className="flex-1 min-w-0 flex flex-col gap-xs">
        {/* Toolbar header with label and Accept/Reject buttons */}
        <DSRichTextToolbar
          textareaTitle={displayLabel}
          bgClass={config.toolbarBgClass}
          borderClass={
            config.toolbarBorderClass || "border-secondary"
          }
          dataTestId={`${dataTestId}-toolbar`}
        >
          <DSDiffActions
            onAccept={onAccept}
            onReject={onReject}
            disabled={isLoading}
            dataTestId={`${dataTestId}-actions`}
          />
        </DSRichTextToolbar>

        <div
          className={`${layoutClass} ${config.bgClass} border-2 border-brand rounded-standard p-md`}
        >
          {/* After content (AI-generated) */}
          <div className={isCompact ? "" : "flex-1"}>
            <h4 className="text-sm font-semibold text-primary mb-xs">
              New
            </h4>
            {isLoading && !afterContent ? (
              <div
                className="p-sm text-sm text-secondary italic"
                style={{
                  minHeight: isCompact ? "80px" : "120px",
                  maxHeight: isCompact ? "200px" : "400px",
                }}
                data-testid={`${dataTestId}-after`}
              >
                Generating content...
              </div>
            ) : afterContent ? (
              <div
                className="p-sm text-sm text-primary overflow-auto"
                style={{
                  minHeight: isCompact ? "80px" : "120px",
                  maxHeight: isCompact ? "200px" : "400px",
                }}
                data-testid={`${dataTestId}-after`}
              >
                <MarkdownContent content={afterContent} />
              </div>
            ) : (
              <div
                className="p-sm text-sm text-secondary italic"
                style={{
                  minHeight: isCompact ? "80px" : "120px",
                  maxHeight: isCompact ? "200px" : "400px",
                }}
                data-testid={`${dataTestId}-after`}
              >
                No content generated yet
              </div>
            )}
          </div>

          {/* Divider */}
          <div
            className={
              isCompact
                ? "border-t border-secondary"
                : "border-l border-secondary"
            }
          />

          {/* Before content (original) */}
          <div className={isCompact ? "" : "flex-1"}>
            <h4 className="text-sm font-semibold text-secondary mb-xs flex items-center gap-xs">
              Original
            </h4>
            {beforeContent ? (
              <div
                className="p-sm text-sm text-secondary overflow-auto italic opacity-75"
                style={{
                  minHeight: isCompact ? "80px" : "120px",
                  maxHeight: isCompact ? "200px" : "400px",
                }}
                data-testid={`${dataTestId}-before`}
              >
                <MarkdownContent content={beforeContent} />
              </div>
            ) : (
              <div
                className="p-sm text-sm text-placeholder"
                style={{
                  minHeight: isCompact ? "80px" : "120px",
                  maxHeight: isCompact ? "200px" : "400px",
                }}
                data-testid={`${dataTestId}-before`}
              >
                No original content
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
